import React, { useEffect, useRef } from 'react';
import { getTriageCurrentUserId, getTriageStaff } from '../../triage/identity';
import type { TriageStaff } from '../../triage/types';
import { Icon } from '../Icon/Icon';
import { TriageAssigneeAvatar } from './TriageAssigneeAvatar';

export interface TriageAssigneeMenuProps {
  open: boolean;
  currentAssigneeId: string | null;
  onSelect: (id: string | null) => void;
  onClose: () => void;
  includeUnassigned?: boolean;
  anchorStyle?: React.CSSProperties;
}

const SELECTED_BG = 'color-mix(in srgb, var(--success) 7%, transparent)';

export function TriageAssigneeMenu({
  open,
  currentAssigneeId,
  onSelect,
  onClose,
  includeUnassigned = true,
  anchorStyle,
}: TriageAssigneeMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const staff = getTriageStaff();
  const currentUserId = getTriageCurrentUserId();
  const me: TriageStaff | undefined = currentUserId ? staff.find((s) => s.id === currentUserId) : undefined;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div ref={menuRef} className="ti-menu" style={{ ...anchorStyle, minWidth: 240 }}>
      <div className="ti-menu-eyebrow">Assign to</div>
      {me ? (
        <>
          <div
            className="ti-menu-item"
            role="button"
            tabIndex={0}
            onClick={() => onSelect(me.id)}
            style={{ background: currentAssigneeId === me.id ? SELECTED_BG : 'transparent' }}
          >
            <TriageAssigneeAvatar staff={me} size={22} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-0)' }}>
                Me ({me.name.split(' ')[0]})
              </div>
              {me.role ? <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{me.role}</div> : null}
            </div>
            {currentAssigneeId === me.id ? (
              <Icon kind="check" size={12} stroke={2.5} color="var(--success)" />
            ) : null}
          </div>
          <div className="ti-menu-item-divider" />
        </>
      ) : null}
      <div className="ti-menu-eyebrow">Team</div>
      <div className="cal-scroll" style={{ maxHeight: 240, overflowY: 'auto' }}>
        {staff
          .filter((s) => s.id !== me?.id)
          .map((s) => (
            <div
              key={s.id}
              className="ti-menu-item"
              role="button"
              tabIndex={0}
              onClick={() => onSelect(s.id)}
              style={{ background: currentAssigneeId === s.id ? SELECTED_BG : 'transparent' }}
            >
              <TriageAssigneeAvatar staff={s} size={22} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-0)' }}>{s.name}</div>
                {s.role ? <div style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{s.role}</div> : null}
              </div>
              {currentAssigneeId === s.id ? (
                <Icon kind="check" size={12} stroke={2.5} color="var(--success)" />
              ) : null}
            </div>
          ))}
      </div>
      {includeUnassigned && currentAssigneeId ? (
        <>
          <div className="ti-menu-item-divider" />
          <div
            className="ti-menu-item"
            role="button"
            tabIndex={0}
            onClick={() => onSelect(null)}
            style={{ color: 'var(--terracotta)' }}
          >
            <Icon kind="x" size={13} stroke={2} color="var(--terracotta)" />
            <span>Unassign</span>
          </div>
        </>
      ) : null}
    </div>
  );
}
